import { useMemo } from "react";
import { Grid, TextField, Typography, Autocomplete as MuiAutocomplete, ListItemButton } from "@mzc-pdc/ui";
import { IconAvata } from "@mzc-cloudplex/icons";
import useIntersection from "@hooks/useIntersection";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import theme from "@styles/theme";

export const AutocompleteUsers = (props) => {
    const {
        value,
        open,
        disabled,
        setValue = () => {},
        onSubmit = () => {},
        onFetchData,
        onClickOption,
        onInputChange = () => {},
        placeholder,
        options,
        setOpen,
        sx,
        isEdit,
        handleClose = () => {},
        setSelectedUser = () => {},
        isAvatarIconRequired,
        PopperComponent,
        noOptionsText,
    } = props;
    const { setTarget } = useIntersection({
        onIntersect: onFetchData,
    });

    const getStartAdornment = useMemo(() => {
        if (!isAvatarIconRequired && !value) return undefined;
        return (
            <Grid item ml={theme.spacing(1)}>
                <IconAvata size={20} htmlColor={"#83839a"} />
            </Grid>
        );
    }, [isAvatarIconRequired, value]);

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <MuiAutocomplete
                clearOnBlur
                open={open}
                value={value}
                onInputChange={(e, value, reason) => {
                    onInputChange(reason);
                }}
                options={
                    options?.map((option, idx) => ({
                        ...option,
                        id: option.id ?? option.title,
                        lastOption: idx === options.length - 1,
                    })) || []
                }
                //page와 size에 따른 스크롤 무한로딩
                PopperComponent={PopperComponent}
                size={`small`}
                disabled={disabled}
                getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option && (option?.name ?? "");
                }}
                onChange={(e, newValue) => {
                    e.stopPropagation();
                    //option을 선택하고 엔터 입력 이벤트
                    if (onSubmit) onClickOption(newValue);
                }}
                noOptionsText={noOptionsText}
                renderOption={(props, option) => {
                    return (
                        <ListItemButton
                            {...props}
                            key={option.id}
                            onClick={() => {
                                if (onSubmit) onClickOption(option);
                            }}
                            ref={onFetchData && option.lastOption ? setTarget : null}
                            selected={value?.id === option.id}
                        >
                            <Grid
                                container
                                flexDirection={"row"}
                                gap={"8px"}
                                flexWrap={"nowrap"}
                                pt={`1px`}
                                pb={`1px`}
                                alignItems={"center"}
                            >
                                <Grid item>
                                    <IconAvata size={18} htmlColor={"#83839a"} />
                                </Grid>
                                <Grid item sx={{ overflow: `hidden` }}>
                                    <Grid container flexDirection={"column"}>
                                        {option.name && (
                                            <Typography
                                                variant={"body1_400"}
                                                color={theme.palette.secondary[800]}
                                                sx={{ wordWrap: `break-word`, wordBreak: `keep-all` }}
                                            >
                                                {option.name}
                                            </Typography>
                                        )}
                                        {option.email && (
                                            <Typography
                                                variant={"body3_400"}
                                                color={theme.palette.secondary[600]}
                                                sx={{ mt: `1px`, wordWrap: `break-word`, wordBreak: `keep-all` }}
                                            >
                                                {option.email}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </ListItemButton>
                    );
                }}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: getStartAdornment,
                            }}
                            placeholder={placeholder}
                            onClick={(e) => {
                                setOpen(true);
                            }}
                            onChange={(e) => {
                                e.stopPropagation();
                                if (onSubmit) {
                                    setSelectedUser(null);
                                    setValue(e.target.value);
                                    onSubmit(e.target.value);
                                    setOpen(true);
                                }
                            }}
                            isEdit={isEdit}
                            sx={{
                                ".MuiInputBase-root": {
                                    paddingTop: `3px !important`,
                                    minHeight: `40px`,
                                },
                                ".MuiInputBase-input": {
                                    border: "none",
                                },
                                "&::placeholder": {
                                    color: theme.palette.secondary[400],
                                    fontWeight: "normal",
                                },
                                ...sx,
                            }}
                        />
                    );
                }}
                slotProps={{
                    popupIndicator: {
                        onClick: (e) => {
                            e.stopPropagation();
                            setOpen(!open);
                        },
                    },
                }}
                {...sx}
            />
        </ClickAwayListener>
    );
};
